import { useState } from 'react'
import './StockTable.css'

const API = process.env.REACT_APP_API || ''

const apiFetch = async (path, opts = {}) => {
    const res = await fetch(API + path, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) },
        ...opts,
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(data.message || `HTTP ${res.status}`)
    return data
}

// rows: [{ variant: {...}, stock: {...} }]
// brandId, productId: pour les appels API
// onUpdated(): callback après mise à jour
const StockTable = ({ rows, brandId, productId, onUpdated }) => {
    const [editing, setEditing] = useState(null) // variantId en cours d'édition
    const [editForm, setEditForm] = useState({})
    const [saving, setSaving] = useState(false)
    const [notifyingVariant, setNotifyingVariant] = useState(null)

    const openEdit = (row) => {
        setEditing(row.variant._id)
        setEditForm({
            qty: row.stock?.qty ?? '',
            lowStockThreshold: row.stock?.lowStockThreshold ?? 5,
        })
    }

    const cancelEdit = () => { setEditing(null); setEditForm({}) }

    const saveEdit = async (variantId) => {
        setSaving(true)
        try {
            await apiFetch(`/brand/${brandId}/products/${productId}/variants/${variantId}/stock`, {
                method: 'PATCH',
                body: JSON.stringify({
                    qty: editForm.qty === '' ? null : parseInt(editForm.qty, 10),
                    lowStockThreshold: parseInt(editForm.lowStockThreshold, 10) || 5,
                }),
            })
            setEditing(null)
            onUpdated && onUpdated()
        } catch (err) {
            alert(err.message)
        } finally {
            setSaving(false)
        }
    }

    const handleToggleVariant = async (row) => {
        try {
            await apiFetch(`/brand/${brandId}/products/${productId}/variants/${row.variant._id}`, {
                method: 'PATCH',
                body: JSON.stringify({ disabled: !row.variant.disabled }),
            })
            onUpdated && onUpdated()
        } catch (err) {
            alert(err.message)
        }
    }

    const handleNotifyWaitlist = async (variantId) => {
        setNotifyingVariant(variantId)
        try {
            await apiFetch(`/brand/${brandId}/products/${productId}/variants/${variantId}/waitlist/notify`, {
                method: 'POST',
            })
            alert('Notifications envoyées à la liste d\'attente.')
        } catch (err) {
            alert(err.message)
        } finally {
            setNotifyingVariant(null)
        }
    }

    const getIndicator = (stock) => {
        if (!stock || stock.qty === null) return 'unlimited'
        const avail = stock.qty - stock.reserved - stock.committed
        if (avail <= 0) return 'red'
        if (avail <= (stock.lowStockThreshold || 5)) return 'orange'
        return 'green'
    }

    const fmtVariant = (v) =>
        [v.color, v.size].filter(Boolean).join(' / ') || v.sku || '—'

    if (!rows || rows.length === 0) {
        return <p className="stock-empty">Aucun variant pour ce produit.</p>
    }

    return (
        <div className="stock-table-wrap">
            <table className="stock-table">
                <thead>
                    <tr>
                        <th>SKU</th>
                        <th>Attributs</th>
                        <th>Prix</th>
                        <th>Dispo</th>
                        <th>Réservé</th>
                        <th>Engagé</th>
                        <th>Seuil</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map(row => {
                        const v = row.variant
                        const s = row.stock
                        const avail = s ? (s.qty === null ? '∞' : s.qty - s.reserved - s.committed) : '—'
                        const indicator = getIndicator(s)
                        const isEditing = editing === v._id

                        return (
                            <tr key={v._id} className={v.disabled ? 'stock-row--disabled' : ''}>
                                <td>
                                    <span className={`stock-indicator stock-indicator--${indicator}`} />
                                    <code className="stock-sku">{v.sku}</code>
                                </td>
                                <td className="stock-attrs">{fmtVariant(v)}</td>
                                <td>{v.price !== null ? `${(v.price / 100).toFixed(2)} €` : '—'}</td>
                                <td>
                                    {isEditing ? (
                                        <input
                                            className="stock-input-inline"
                                            type="number" min="0"
                                            placeholder="∞ si vide"
                                            value={editForm.qty}
                                            onChange={e => setEditForm(f => ({ ...f, qty: e.target.value }))}
                                        />
                                    ) : (
                                        <span className={`stock-qty stock-qty--${indicator}`}>{avail}</span>
                                    )}
                                </td>
                                <td>{s?.reserved ?? '—'}</td>
                                <td>{s?.committed ?? '—'}</td>
                                <td>
                                    {isEditing ? (
                                        <input
                                            className="stock-input-inline"
                                            type="number" min="0"
                                            value={editForm.lowStockThreshold}
                                            onChange={e => setEditForm(f => ({ ...f, lowStockThreshold: e.target.value }))}
                                        />
                                    ) : (
                                        s?.lowStockThreshold ?? '—'
                                    )}
                                </td>
                                <td className="stock-actions">
                                    {isEditing ? (
                                        <>
                                            <button
                                                className="stock-btn-save"
                                                onClick={() => saveEdit(v._id)}
                                                disabled={saving}
                                            >
                                                {saving ? '...' : '✓'}
                                            </button>
                                            <button className="stock-btn-cancel" onClick={cancelEdit}>✕</button>
                                        </>
                                    ) : (
                                        <>
                                            <button
                                                className="stock-btn-edit"
                                                onClick={() => openEdit(row)}
                                                title="Modifier le stock"
                                            >
                                                ✏️
                                            </button>
                                            <button
                                                className={`stock-btn-toggle${v.disabled ? ' stock-btn-toggle--off' : ''}`}
                                                onClick={() => handleToggleVariant(row)}
                                                title={v.disabled ? 'Activer' : 'Désactiver'}
                                            >
                                                {v.disabled ? 'Activer' : 'Désact.'}
                                            </button>
                                            {indicator === 'red' && (
                                                <button
                                                    className="stock-btn-notify"
                                                    onClick={() => handleNotifyWaitlist(v._id)}
                                                    disabled={notifyingVariant === v._id}
                                                    title="Notifier la liste d'attente"
                                                >
                                                    {notifyingVariant === v._id ? '...' : '🔔'}
                                                </button>
                                            )}
                                        </>
                                    )}
                                </td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        </div>
    )
}

export default StockTable
